import { useEffect, useMemo, useState } from "react";
import { useGetList } from "ra-core";

import { getActiveWorkspaceId } from "../workspace/activeWorkspace";

/**
 * Pipelines for the active workspace, plus which one the board is showing.
 *
 * The selection is remembered per workspace (switching tenants must not carry
 * a pipeline id across, since ids are workspace-scoped), and falls back to the
 * workspace's default pipeline whenever the stored one is gone or unknown.
 */

export interface Pipeline {
  id: string;
  workspace_id: string;
  name: string;
  description: string | null;
  is_default: boolean;
  active: boolean;
  position: number;
}

export interface PipelineStage {
  id: string;
  pipeline_id: string;
  value: string;
  label: string;
  position: number;
  outcome: "open" | "won" | "lost";
  requirements?: {
    require_amount?: boolean;
    require_expected_close?: boolean;
    require_contact?: boolean;
    min_probability?: number;
  } | null;
}

const storageKey = (workspaceId: string | null) =>
  `gravun.activePipeline.${workspaceId ?? "none"}`;

export const usePipelines = () => {
  const workspaceId = getActiveWorkspaceId();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data: pipelines, isPending } = useGetList<Pipeline>("pipelines", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "position", order: "ASC" },
  });

  // Resolve the selection once pipelines are known: stored choice if it still
  // exists, otherwise the workspace default, otherwise the first one.
  useEffect(() => {
    if (!pipelines?.length) return;
    const stored =
      typeof window !== "undefined"
        ? window.localStorage.getItem(storageKey(workspaceId))
        : null;
    const valid = pipelines.some((p) => p.id === stored);
    const fallback =
      pipelines.find((p) => p.is_default)?.id ?? pipelines[0]?.id ?? null;
    setSelectedId(valid ? stored : fallback);
  }, [pipelines, workspaceId]);

  const selectPipeline = (id: string) => {
    setSelectedId(id);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(storageKey(workspaceId), id);
    }
  };

  const { data: allStages } = useGetList<PipelineStage>("pipeline_stages", {
    pagination: { page: 1, perPage: 200 },
    sort: { field: "position", order: "ASC" },
  });

  const stages = useMemo(
    () =>
      (allStages ?? [])
        .filter((stage) => stage.pipeline_id === selectedId)
        .sort((a, b) => a.position - b.position),
    [allStages, selectedId],
  );

  return {
    isPending,
    pipelines: pipelines ?? [],
    selectedId,
    selectPipeline,
    selected: (pipelines ?? []).find((p) => p.id === selectedId) ?? null,
    stages,
  };
};

/**
 * The kanban speaks `{ value, label }`. Falling back to the app configuration
 * keeps the board working for a workspace whose pipeline has no stages yet,
 * rather than rendering an empty screen.
 */
export const stagesForBoard = (
  stages: PipelineStage[],
  fallback: { value: string; label: string }[],
): { value: string; label: string }[] =>
  stages.length
    ? stages.map((stage) => ({ value: stage.value, label: stage.label }))
    : fallback;
